import clsx from "clsx";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { ReactComponent as ArrowRightIcon } from "../../../../../../assets/icons/arrowhead-right-icon.svg";
import useTranslation from "../../../../../../hooks/useTranslation";
import { getPagePath } from "../../../../../../routing/routesUtils";
import { externalReferenceNewTag } from "../../../../../../utils/cmsUtils";
import CustomText from "../../../../../styled-UI/CustomText";
import NewTag from "../../../../../widgets/tutorial-pills/new-tag/NewTag";
import { HeaderListContainer } from "./MenuColumn";
import { CategoryColumnInfo } from "../../../../../../hooks/useMenuColumnsInfo";
import LinkButton from "../../../../../styled-UI/LinkButton";
import { resetScrollPosition } from "../../../../../../hooks/useSaveScrollPosition";
import { composeUrlWithParams } from "../../../../../../utils/utils";
import { MenuColumn as MenuColumnInterface } from "./menuStructure";
import MemoizedCloseButton from "../../../../../widgets/CloseButton";

interface Props {
  categoryColumns: CategoryColumnInfo[];
  baseUrl: string;
  columnStructure: MenuColumnInterface;
  identifier: string;
  title: string;
  toggleMenu: (show: boolean) => void;
  setToggleMenu: (identifier: string) => void;
  setCurrent: (current: string) => void;
  current: string;
}

const SubCategoryColumn = ({
  baseUrl,
  columnStructure,
  categoryColumns,
  identifier,
  title,
  toggleMenu,
  setToggleMenu,
  setCurrent,
  current,
}: Props): JSX.Element => {
  const { translateLabel } = useTranslation();
  const history = useHistory();
  const isOpen = title === current;

  /* PRINT VIEW ALL BUTTON */
  const printViewAll = () => {
    const filters: { key: string; value: string }[] = [];

    /*  ADD FILTERS IF PRESENT IN COLUMN  */
    if (columnStructure.facet?.facetName && columnStructure.facet?.value) {
      filters.push({
        key: columnStructure.facet?.facetName as string,
        value: columnStructure.facet?.value as string,
      });
    }

    /*  PRINT VIEW ALL BUTTON FOR EACH COLUMN */
    return (
      <ViewAllContainer>
        <LinkButton
          uppercase
          underline={false}
          onClick={() => {
            toggleMenu(false);
            setToggleMenu("");
            resetScrollPosition();
            history.push(getPagePath(composeUrlWithParams(baseUrl, filters)));
          }}
          data-element-id={`MainNav_Products_${identifier}_${title}_ViewAll`}
        >
          {translateLabel("VIEW_ALL")}
        </LinkButton>
      </ViewAllContainer>
    );
  };

  return (
    <Container>
      {categoryColumns && (
        <UrlList isOpen={isOpen}>
          {identifier !== "CATEGORIES" && (
            <HeaderListContainer
              className={clsx(isOpen && "is-selected")}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(title);
              }}
            >
              <>
                <CustomText
                  fontSizePx={14}
                  lineHeightPx={17}
                  as="span"
                  data-element-id={`MainNav_Products_${identifier}_${title}`}
                  font="font-regular"
                >
                  {translateLabel(title)}
                </CustomText>
                <NewTag position={externalReferenceNewTag[`${identifier}.${title}`]}></NewTag>
                <ArrowRightIcon style={{ marginLeft: "auto" }} />
              </>
            </HeaderListContainer>
          )}
          {isOpen && (
            <ListItemsContainer className={clsx(identifier !== "CATEGORIES" && "other-categories")}>
              {categoryColumns.map((item, index: number) => {
                return (
                  <React.Fragment key={item.keyColumn}>
                    <div>
                      <li
                        key={item.keyColumn}
                        onClick={() => {
                          setToggleMenu("");
                        }}
                      >
                        <Link to={getPagePath(item.link)} data-element-id={item.dataElementId}>
                          <CustomText
                            as="span"
                            font="font-regular"
                            fontSizePx={14}
                            lineHeightPx={18}
                            isClickable
                            isEllipsis
                          >
                            {item.label}
                          </CustomText>
                        </Link>
                      </li>
                    </div>
                  </React.Fragment>
                );
              })}
              {columnStructure.viewAllButton && printViewAll()}
              <MemoizedCloseButton
                action={() => setToggleMenu("")}
                dataElementId="MainNav_Menu_Close"
                lastItem
                type={"default"}
              />
            </ListItemsContainer>
          )}
        </UrlList>
      )}
    </Container>
  );
};

const Container = styled.div``;

export const UrlList = styled.ul<{ isOpen: boolean }>`
  li {
    min-width: 10rem;
    /*max-width: 14rem;*/
  }
  ${(props) =>
    props.isOpen &&
    `&::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 199%;
    width: 100vw;
    background-color: ${props.theme.palette.white};`}
`;

const ListItemsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.textBlue};

  div span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  span {
    border-bottom: 1px solid transparent;
    &:hover {
      font-weight: bold;
      border-bottom: 1px solid ${(props) => props.theme.palette.primary};
    }
  }

  &.other-categories {
    gap: 1.25rem;
    box-shadow: ${(props) => props.theme.palette.menuBoxShadow};
    background-color: ${(props) => props.theme.palette.white};
    position: absolute;
    top: 0;
    left: 29.688rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    height: 100%;

    overflow-y: auto;
    padding: 2.5rem 2rem 2rem 2rem;
    border-bottom: unset;
    span {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media ${(props) => props.theme.queries.md} {
      left: calc(29.688rem - 3.5rem);
    }

    @media ${(props) => props.theme.queries.minLaptop} {
      left: 33vw;
      width: 33vw;
    }
  }
`;

const ViewAllContainer = styled.div`
  display: flex;
  justify-content: left;
  button {
    border-bottom: 1px solid ${(props) => props.theme.palette.primary};
  }
`;

export default SubCategoryColumn;
